import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Search, UserCog, ShieldCheck, Save, X, AlertTriangle, History, CheckSquare, Square } from "lucide-react";
import api from "../lib/api";
import { initials, formatDate } from "../lib/utils";

export default function CoordinatorsPage() {
  const [tab, setTab] = useState("coordinadores");
  const [coords, setCoords] = useState([]);
  const [areas, setAreas] = useState([]);
  const [q, setQ] = useState("");
  const [filterEstado, setFilterEstado] = useState("");
  const [filterArea, setFilterArea] = useState("");
  const [confirmOff, setConfirmOff] = useState(null);
  const [permsFor, setPermsFor] = useState(null);

  const load = async () => {
    try {
      const [c, a] = await Promise.all([api.get("/coordinators"), api.get("/areas")]);
      setCoords(c.data); setAreas(a.data);
    } catch { toast.error("Error al cargar coordinadores"); }
  };
  useEffect(() => { load(); }, []);

  const rows = coords.filter(c => {
    if (q && !(`${c.name} ${c.email}`.toLowerCase().includes(q.toLowerCase()))) return false;
    if (filterEstado === "activo" && c.active === false) return false;
    if (filterEstado === "inactivo" && c.active !== false) return false;
    if (filterArea && c.area_id !== filterArea) return false;
    return true;
  });

  const toggleActive = async (c) => {
    if (c.active !== false) { setConfirmOff(c); return; }
    try {
      await api.put(`/users/${c.id}`, { active: true });
      toast.success(`Cuenta de ${c.name} habilitada`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al habilitar"); }
  };

  const confirmDisable = async () => {
    try {
      await api.put(`/users/${confirmOff.id}`, { active: false });
      toast.success(`Cuenta de ${confirmOff.name} deshabilitada`);
      setConfirmOff(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al deshabilitar"); }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Administración</div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">Gestión de Coordinadores</h1>
        <p className="text-sm text-slate-500 mt-1">Controle el estado de las cuentas coordinador y su alcance sobre los colaboradores.</p>
      </div>

      <div className="flex gap-1 border-b border-slate-200">
        <button onClick={() => setTab("coordinadores")} data-testid="tab-coordinadores"
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px inline-flex items-center gap-2 ${tab === "coordinadores" ? "border-crejpp text-crejpp" : "border-transparent text-slate-500 hover:text-slate-900"}`}>
          <UserCog className="w-4 h-4" /> Coordinadores
        </button>
        <button onClick={() => setTab("historial")} data-testid="tab-historial"
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px inline-flex items-center gap-2 ${tab === "historial" ? "border-crejpp text-crejpp" : "border-transparent text-slate-500 hover:text-slate-900"}`}>
          <History className="w-4 h-4" /> Historial de cambios
        </button>
      </div>

      {tab === "coordinadores" ? (
        <>
          <div className="bg-white border border-slate-200 rounded-lg p-4 flex flex-wrap gap-3 items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar por nombre o correo…"
                className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-md text-sm" data-testid="coord-search" />
            </div>
            <select value={filterEstado} onChange={e => setFilterEstado(e.target.value)} className="text-sm px-3 py-2 border border-slate-300 rounded-md" data-testid="coord-filter-estado">
              <option value="">Todos los estados</option>
              <option value="activo">Activo</option>
              <option value="inactivo">Inactivo</option>
            </select>
            <select value={filterArea} onChange={e => setFilterArea(e.target.value)} className="text-sm px-3 py-2 border border-slate-300 rounded-md" data-testid="coord-filter-area">
              <option value="">Todas las áreas</option>
              {areas.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
            <table className="w-full text-sm min-w-[820px]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200 bg-slate-50">
                  <th className="px-4 py-3 font-semibold">Coordinador</th>
                  <th className="px-4 py-3 font-semibold">Área</th>
                  <th className="px-4 py-3 font-semibold">Estado</th>
                  <th className="px-4 py-3 font-semibold text-center">Colab. visibles</th>
                  <th className="px-4 py-3 font-semibold text-center">Colab. asignables</th>
                  <th className="px-4 py-3 font-semibold text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(c => (
                  <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`coord-row-${c.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-slate-900 text-white text-xs font-semibold flex items-center justify-center">{initials(c.name)}</div>
                        <div>
                          <div className="font-medium text-slate-900">{c.name}</div>
                          <div className="text-xs text-slate-500">{c.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{c.area_name || "—"}</td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => toggleActive(c)}
                        data-testid={`coord-toggle-${c.id}`}
                        title={c.active !== false ? "Deshabilitar cuenta" : "Habilitar cuenta"}
                        className={`inline-flex items-center gap-2 text-xs font-semibold rounded-full pl-1.5 pr-3 py-1 border transition-colors ${c.active !== false ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100" : "bg-slate-100 text-slate-500 border-slate-200 hover:bg-slate-200"}`}
                      >
                        <span className={`relative inline-flex h-4 w-7 rounded-full transition-colors ${c.active !== false ? "bg-emerald-500" : "bg-slate-300"}`}>
                          <span className={`absolute top-0.5 h-3 w-3 rounded-full bg-white transition-transform ${c.active !== false ? "translate-x-3.5" : "translate-x-0.5"}`} />
                        </span>
                        {c.active !== false ? "Activo" : "Inactivo"}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-center font-semibold text-slate-900">{c.n_visibles}</td>
                    <td className="px-4 py-3 text-center font-semibold text-slate-900">{c.n_asignables}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => setPermsFor(c)}
                        data-testid={`coord-perms-${c.id}`}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold border border-slate-300 rounded-md px-3 py-1.5 hover:border-crejpp hover:text-crejpp transition-colors"
                      >
                        <ShieldCheck className="w-3.5 h-3.5" /> Configurar permisos
                      </button>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr><td colSpan="6" className="px-4 py-14 text-center text-sm text-slate-400">No hay coordinadores que coincidan con los filtros.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <HistoryTab coords={coords} />
      )}

      {confirmOff && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setConfirmOff(null)}>
          <div className="bg-white rounded-lg border border-slate-200 w-full max-w-md p-6" onClick={e => e.stopPropagation()} data-testid="disable-coord-modal">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-10 h-10 rounded-md bg-amber-50 text-amber-600 flex items-center justify-center shrink-0"><AlertTriangle className="w-5 h-5" /></div>
              <h3 className="font-heading text-lg font-bold text-slate-900">Deshabilitar cuenta</h3>
            </div>
            <p className="text-sm text-slate-600">
              ¿Está seguro de deshabilitar esta cuenta? El coordinador <b className="text-slate-900">{confirmOff.name}</b> no podrá iniciar sesión.
            </p>
            <p className="text-xs text-slate-500 mt-2">Sus datos, tareas creadas y asignadas se conservan y seguirán visibles para usted.</p>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setConfirmOff(null)} className="px-4 py-2 border border-slate-300 rounded-md text-sm bg-white hover:bg-slate-100" data-testid="disable-coord-cancel">Cancelar</button>
              <button onClick={confirmDisable} className="px-4 py-2 bg-crejpp hover:bg-crejpp-dark text-white rounded-md text-sm font-semibold" data-testid="disable-coord-confirm">Deshabilitar</button>
            </div>
          </div>
        </div>
      )}

      {permsFor && (
        <PermissionsModal coord={permsFor} areas={areas} onClose={() => setPermsFor(null)} onSaved={() => { setPermsFor(null); load(); }} />
      )}
    </div>
  );
}

function PermissionsModal({ coord, areas, onClose, onSaved }) {
  const [colabs, setColabs] = useState([]);
  const [perms, setPerms] = useState({});
  const [areaFilter, setAreaFilter] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/coordinators/${coord.id}/permissions`);
        setColabs(data.colaboradores);
        const map = {};
        Object.entries(data.permisos || {}).forEach(([cid, p]) => {
          map[cid] = { puede_visualizar: !!p.puede_visualizar, puede_asignar: !!p.puede_asignar };
        });
        setPerms(map);
      } catch { toast.error("Error al cargar permisos"); }
      finally { setLoading(false); }
    })();
  }, [coord.id]);

  const areaMap = Object.fromEntries(areas.map(a => [a.id, a.name]));
  const visible = colabs.filter(c => !areaFilter || c.area_id === areaFilter);

  const get = (id) => perms[id] || { puede_visualizar: false, puede_asignar: false };

  const setVis = (id, val) => setPerms(p => {
    const cur = { ...get(id), puede_visualizar: val };
    if (!val) cur.puede_asignar = false;
    return { ...p, [id]: cur };
  });
  const setAsig = (id, val) => setPerms(p => {
    const cur = { ...get(id), puede_asignar: val };
    if (val) cur.puede_visualizar = true;
    return { ...p, [id]: cur };
  });

  const selectAll = () => setPerms(p => {
    const next = { ...p };
    visible.forEach(c => { next[c.id] = { puede_visualizar: true, puede_asignar: true }; });
    return next;
  });
  const clearAll = () => setPerms(p => {
    const next = { ...p };
    visible.forEach(c => { next[c.id] = { puede_visualizar: false, puede_asignar: false }; });
    return next;
  });

  const save = async () => {
    setSaving(true);
    try {
      const permisos = colabs.map(c => ({
        colaborador_id: c.id,
        puede_visualizar: get(c.id).puede_visualizar,
        puede_asignar: get(c.id).puede_asignar,
      }));
      await api.put(`/coordinators/${coord.id}/permissions`, { permisos });
      toast.success("Permisos guardados correctamente");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar los permisos");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg border border-slate-200 w-full max-w-2xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="perms-modal">
        <div className="flex items-start justify-between p-5 border-b border-slate-200">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Permisos sobre colaboradores</div>
            <h3 className="font-heading text-lg font-bold text-slate-900">{coord.name}</h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-md" data-testid="perms-close"><X className="w-4 h-4" /></button>
        </div>

        <div className="px-5 py-3 border-b border-slate-100 flex flex-wrap items-center gap-2">
          <select value={areaFilter} onChange={e => setAreaFilter(e.target.value)} className="text-xs px-2.5 py-1.5 border border-slate-300 rounded-md" data-testid="perms-area-filter">
            <option value="">Todas las áreas</option>
            {areas.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          <button onClick={selectAll} className="inline-flex items-center gap-1.5 text-xs font-semibold border border-slate-300 rounded-md px-3 py-1.5 hover:border-crejpp hover:text-crejpp" data-testid="perms-select-all">
            <CheckSquare className="w-3.5 h-3.5" /> Seleccionar todos
          </button>
          <button onClick={clearAll} className="inline-flex items-center gap-1.5 text-xs font-semibold border border-slate-300 rounded-md px-3 py-1.5 hover:border-crejpp hover:text-crejpp" data-testid="perms-clear-all">
            <Square className="w-3.5 h-3.5" /> Quitar todos
          </button>
          <span className="ml-auto text-[11px] text-slate-500">«Puede asignar tareas» activa automáticamente «Puede visualizar».</span>
        </div>

        <div className="overflow-y-auto divide-y divide-slate-100 flex-1">
          {loading && <div className="p-8 text-center text-sm text-slate-400">Cargando…</div>}
          {!loading && visible.length === 0 && <div className="p-8 text-center text-sm text-slate-400">No hay colaboradores activos con este filtro.</div>}
          {!loading && visible.map(c => {
            const p = get(c.id);
            return (
              <div key={c.id} className="px-5 py-3 flex items-center gap-3 flex-wrap" data-testid={`perm-row-${c.id}`}>
                <div className="flex items-center gap-2.5 flex-1 min-w-[200px]">
                  <div className="w-8 h-8 rounded-full bg-slate-900 text-white text-xs font-semibold flex items-center justify-center shrink-0">{initials(c.name)}</div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">{c.name}</div>
                    <div className="text-[11px] text-slate-500">{areaMap[c.area_id] || "Sin área"}</div>
                  </div>
                </div>
                <label className="inline-flex items-center gap-2 text-xs text-slate-700 cursor-pointer select-none">
                  <input type="checkbox" checked={p.puede_visualizar} onChange={e => setVis(c.id, e.target.checked)}
                    className="w-4 h-4 accent-[#EE1C25]" data-testid={`perm-vis-${c.id}`} />
                  Puede visualizar
                </label>
                <label className="inline-flex items-center gap-2 text-xs text-slate-700 cursor-pointer select-none">
                  <input type="checkbox" checked={p.puede_asignar} onChange={e => setAsig(c.id, e.target.checked)}
                    className="w-4 h-4 accent-[#EE1C25]" data-testid={`perm-asig-${c.id}`} />
                  Puede asignar tareas
                </label>
              </div>
            );
          })}
        </div>

        <div className="border-t border-slate-200 p-4 flex justify-end gap-2 bg-slate-50">
          <button onClick={onClose} className="px-4 py-2 border border-slate-300 rounded-md text-sm bg-white hover:bg-slate-100">Cancelar</button>
          <button onClick={save} disabled={saving} className="px-4 py-2 bg-crejpp hover:bg-crejpp-dark disabled:opacity-50 text-white rounded-md text-sm font-semibold inline-flex items-center gap-1.5" data-testid="perms-save">
            <Save className="w-4 h-4" /> {saving ? "Guardando…" : "Guardar cambios"}
          </button>
        </div>
      </div>
    </div>
  );
}

function HistoryTab({ coords }) {
  const [items, setItems] = useState([]);
  const [coordId, setCoordId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [loading, setLoading] = useState(true);

  const coordIds = useMemo(() => new Set(coords.map(c => c.id)), [coords]);
  const coordMap = useMemo(() => Object.fromEntries(coords.map(c => [c.id, c.name])), [coords]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/audit");
        const rel = data.filter(a =>
          a.entity === "coordinator_permissions" ||
          (a.entity === "user" && ["activate", "deactivate"].includes(a.action) && coordIds.has(a.entity_id))
        );
        setItems(rel);
      } catch { toast.error("Error al cargar el historial"); }
      finally { setLoading(false); }
    })();
  }, [coordIds]);

  const rows = items.filter(a => {
    if (coordId && a.entity_id !== coordId) return false;
    if (from && a.timestamp < from) return false;
    if (to && a.timestamp > to + "T23:59:59") return false;
    return true;
  });

  const label = (a) => {
    if (a.action === "deactivate") return "Cuenta deshabilitada";
    if (a.action === "activate") return "Cuenta habilitada";
    return "Permisos modificados";
  };

  return (
    <div className="space-y-4">
      <div className="bg-white border border-slate-200 rounded-lg p-4 flex flex-wrap gap-3 items-center">
        <select value={coordId} onChange={e => setCoordId(e.target.value)} className="text-sm px-3 py-2 border border-slate-300 rounded-md" data-testid="hist-filter-coord">
          <option value="">Todos los coordinadores</option>
          {coords.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <input type="date" value={from} onChange={e => setFrom(e.target.value)} className="text-sm px-3 py-2 border border-slate-300 rounded-md" data-testid="hist-from" />
        <input type="date" value={to} onChange={e => setTo(e.target.value)} className="text-sm px-3 py-2 border border-slate-300 rounded-md" data-testid="hist-to" />
      </div>

      <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100">
        {loading && <div className="p-10 text-center text-sm text-slate-400">Cargando…</div>}
        {!loading && rows.map(a => (
          <div key={a.id} className="p-4 flex items-start gap-3" data-testid={`hist-row-${a.id}`}>
            <div className={`w-9 h-9 rounded-md flex items-center justify-center shrink-0 ${a.action === "deactivate" ? "bg-red-50 text-crejpp" : a.action === "activate" ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-600"}`}>
              {a.entity === "coordinator_permissions" ? <ShieldCheck className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm text-slate-800">
                <span className="font-semibold">{label(a)}</span>
                {" · "}
                <span>{coordMap[a.entity_id] || a.details?.coordinador || "Coordinador"}</span>
              </div>
              {a.details?.cambios?.length > 0 && (
                <ul className="text-xs text-slate-500 mt-1 space-y-0.5 list-disc ml-4">
                  {a.details.cambios.map((c, i) => <li key={i}>{c}</li>)}
                </ul>
              )}
              <div className="text-[11px] text-slate-400 mt-1">
                Por {a.user_name} · {formatDate(a.timestamp)} · {String(a.timestamp).slice(11, 19)} UTC
              </div>
            </div>
          </div>
        ))}
        {!loading && rows.length === 0 && <div className="p-10 text-center text-sm text-slate-400">Sin cambios registrados con los filtros seleccionados.</div>}
      </div>
    </div>
  );
}
